"use client";

import { Copy } from "lucide-react";
import type { AppJournalEntry } from "@/vybe-trader-client/messaging/stores/useJournalStore";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/shadcn-components/ui/context-menu";
import { cn } from "@/lib/utils";
import {
  buildJournalCopyText,
  directionGlyph,
  formatJournalTrimmedPayload,
  formatLogTime,
  getEntryDirection,
  getEntryType,
} from "./journalEntryUtils";

interface JournalLogLineProps {
  entry: AppJournalEntry;
  className?: string;
}

function levelMessageClass(level: AppJournalEntry["level"]): string {
  if (level === "error") return "text-destructive";
  if (level === "warning") return "text-amber-500 dark:text-amber-400";
  return "text-foreground/90";
}

export function JournalLogLine({ entry, className }: JournalLogLineProps) {
  const direction = getEntryDirection(entry);
  const entryType = getEntryType(entry);
  const trimmedPayload = formatJournalTrimmedPayload(entry);
  const tooltip = trimmedPayload
    ? `${entry.message}\n${trimmedPayload}`
    : entry.message;

  const handleCopy = () => {
    const text = buildJournalCopyText(entry);
    void navigator.clipboard.writeText(text);
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          title={tooltip || undefined}
          className={cn(
            "flex h-5 items-center gap-2 border-b border-border/40 px-2 font-mono text-[11px] leading-5",
            "cursor-context-menu hover:bg-muted/20",
            className
          )}
        >
          <span className="shrink-0 text-muted-foreground tabular-nums">
            {formatLogTime(entry.timestamp)}
          </span>
          <span
            className="w-3 shrink-0 text-center text-muted-foreground"
            aria-hidden
          >
            {directionGlyph(direction)}
          </span>
          <span className="w-36 shrink-0 truncate text-muted-foreground">
            {entryType}
          </span>
          <span
            className={cn(
              "max-w-[14rem] shrink-0 truncate sm:max-w-[18rem]",
              levelMessageClass(entry.level)
            )}
          >
            {entry.message}
          </span>
          {trimmedPayload ? (
            <span className="min-w-0 flex-1 truncate text-muted-foreground/80">
              {trimmedPayload}
            </span>
          ) : null}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-44">
        <ContextMenuLabel className="text-xs">Journal</ContextMenuLabel>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={handleCopy} className="text-xs">
          <Copy className="mr-2 h-3.5 w-3.5" />
          Copy
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
